import { ChangeDetectorRef, Component, OnInit, signal, ViewChild, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    ApprovalGroupService,
    ApprovalGroup,
    ApprovalGroupContact,
    ApprovalGroupContactChanges,
} from "./service/approval.group.service";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25TableModule } from "../s25-table/s25.table.module";
import { Table } from "../s25-table/Table";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { S25Util } from "../../util/s25-util";
import { DropDownItem } from "../../pojo/DropDownItem";
import { ContactService } from "../../services/contact.service";
import { ResponseMessage } from "../../dataaccess/data.access";

@TypeManagerDecorator("approval-groups")
@Component({
    selector: "approval-groups",
    template: `
        <div class="approval-groups-page">
            @if (!selectedGroup()) {
                <!-- List Mode -->
                <div class="list-mode">
                    <div class="page-header">
                        <h1>Approval Groups</h1>
                        <button class="aw-button aw-button--primary" (click)="createNewGroup()">
                            Create New Group
                        </button>
                    </div>

                    <div class="groups-table-container">
                        <s25-ng-table
                            #groupsTable
                            [dataSource]="tableConfig"
                            [columnSortable]="true"
                            [hasFilter]="true"
                            [hasRefresh]="true"
                        ></s25-ng-table>
                    </div>
                </div>
            } @else {
                <!-- Edit/Create Mode -->
                <div class="edit-mode">
                    <div class="editor-header">
                        <h1>{{ isNewGroup() ? "Create New Approval Group" : "Edit Approval Group" }}</h1>
                    </div>

                    <div class="editor-content">
                        <!-- Basic Info -->
                        <div class="group-basic-info">
                            <div class="form-group">
                                <label for="groupName">Group Name *</label>
                                <input
                                    id="groupName"
                                    type="text"
                                    [(ngModel)]="selectedGroup()!.name"
                                    class="form-control"
                                    required
                                    placeholder="Enter unique group name"
                                />
                            </div>
                        </div>

                        <!-- Group Contacts -->
                        <div class="group-contacts-section">
                            <div class="section-header">
                                <h3>Group Contacts</h3>
                            </div>

                            <div class="contacts-content">
                                <div class="form-group">
                                    <label>Add Contact</label>
                                    <div class="contact-selector">
                                        <s25-contact-dropdown
                                            [(chosen)]="selectedContactForGroup"
                                            (chosenChange)="onContactSelectedForGroup($event)"
                                            class="form-control"
                                        ></s25-contact-dropdown>
                                    </div>
                                </div>

                                @if (selectedGroup()?.contacts && selectedGroup()!.contacts.length > 0) {
                                    <div class="group-contacts">
                                        <h4>Current Contacts ({{ selectedGroup()!.contacts.length }})</h4>
                                        <div class="contacts-list">
                                            @for (contact of selectedGroup()!.contacts; track contact.contId) {
                                                <div class="contact-item">
                                                    <span class="contact-name">{{ contact.contactName }}</span>
                                                    <button
                                                        class="aw-button aw-button--danger--outline contact-remove"
                                                        (click)="removeContactFromGroup(contact.contId)"
                                                        title="Remove contact"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                } @else {
                                    <div class="no-contacts">No contacts added to this group yet.</div>
                                }
                            </div>
                        </div>
                    </div>

                    <!-- Save and Cancel Buttons at Bottom -->
                    <div class="editor-footer">
                        <button class="aw-button aw-button--primary" (click)="saveGroup()">Save Group</button>
                        <button class="aw-button aw-button--danger--outline" (click)="cancelEdit()">Cancel</button>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .approval-groups-page {
            height: 100vh;
            display: flex;
            flex-direction: column;
        }

        /* List Mode */
        .list-mode {
            height: 100%;
            display: flex;
            flex-direction: column;
        }

        .page-header {
            padding: 20px;
            background: white;
            border-bottom: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .page-header h1 {
            margin: 0;
            color: #333;
        }

        .groups-table-container {
            flex: 1;
            padding: 20px;
            background: white;
            overflow-y: auto;
        }

        /* Edit Mode */
        .edit-mode {
            height: 100%;
            display: flex;
            flex-direction: column;
        }

        .editor-header {
            padding: 20px;
            background: white;
            border-bottom: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .editor-header h1 {
            margin: 0;
            color: #333;
        }

        .editor-actions {
            display: flex;
            gap: 8px;
        }

        .editor-content {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
            background: white;
        }

        .editor-footer {
            padding: 20px;
            background: white;
            border-top: 1px solid #ddd;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
        }

        .group-basic-info {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
        }

        .group-contacts-section {
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            overflow: hidden;
        }

        .section-header {
            padding: 16px;
            background: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
        }

        .section-header h3 {
            margin: 0;
            color: #333;
        }

        .contacts-content {
            padding: 20px;
        }

        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-control:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .contact-selector {
            max-width: 400px;
        }

        .group-contacts h4 {
            margin: 0 0 16px 0;
            color: #333;
            font-size: 16px;
        }

        .contacts-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .contact-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
        }

        .contact-name {
            font-weight: 500;
            color: #333;
        }

        .contact-remove {
            font-size: 12px;
            padding: 4px 8px;
        }

        .no-contacts {
            text-align: center;
            padding: 40px;
            color: #666;
            font-style: italic;
            background: #f8f9fa;
            border: 1px dashed #dee2e6;
            border-radius: 4px;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25DropdownPaginatedModule, S25TableModule],
    encapsulation: ViewEncapsulation.Emulated,
})
export class ApprovalGroupsComponent implements OnInit {
    @ViewChild("groupsTable")
    groupsTableComponent!: S25TableComponent;

    constructor(private cd: ChangeDetectorRef) {}

    selectedGroup = signal<ApprovalGroup | null>(null);
    selectedGroupId = signal<number | null>(null);
    selectedContactForGroup: DropDownItem | null = null;
    tableConfig: Table.DataSource;

    // Track original contacts to calculate differential changes
    private originalContacts: ApprovalGroupContact[] = [];
    private contactsToAdd: ApprovalGroupContact[] = [];
    private contactsToRemove: ApprovalGroupContact[] = [];

    isNewGroup = () => !this.selectedGroupId();

    ngOnInit() {
        this.initializeTableConfig();
    }

    private initializeTableConfig() {
        const columns: Table.Column[] = [
            { id: "name", header: "Name" },
            { id: "contacts", header: "Contacts" },
            { id: "lastModDate", header: "Last Modified" },
            { id: "lastModUser", header: "Modified By" },
            GenericTableButtonComponent.Column("Edit", this.onEditClick.bind(this), "outline"),
            GenericTableButtonComponent.Column("Delete", this.onDeleteClick.bind(this), "danger--outline"),
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getApprovalGroupsData.bind(this),
        };
    }

    async getApprovalGroupsData(): Promise<Table.DataSourceResponse> {
        const groups = await ApprovalGroupService.getApprovalGroups();
        const rows: Table.Row[] = groups.map((group) => ({
            id: group.approvalGroupId!,
            name: group.name,
            cells: {
                name: { text: group.name },
                contacts: { text: (group.contacts?.length || 0).toString() },
                lastModDate: { text: group.lastModDate ? new Date(group.lastModDate).toLocaleDateString() : "" },
                lastModUser: { text: group.lastModUser || "" },
            },
            data: group,
        }));
        return {
            rows: rows,
            totalRows: rows.length,
        };
    }

    async onEditClick(row: Table.Row) {
        const group = row.data as ApprovalGroup;
        await this.selectGroup(group);
    }

    async onDeleteClick(row: Table.Row) {
        const groupId = row.id as number;
        const groupName = row.data.name;

        if (
            !confirm(`Are you sure you want to delete the approval group "${groupName}"? This action cannot be undone.`)
        ) {
            return;
        }

        try {
            const result = await ApprovalGroupService.deleteApprovalGroup(groupId);
            if (result.message) {
                await this.refresh();
            } else {
                S25Util.showError(result.message);
            }
        } catch (error) {
            console.error("Error deleting approval group:", error);
            S25Util.showError(error);
        }
    }

    async selectGroup(group: ApprovalGroup) {
        this.selectedGroupId.set(group.approvalGroupId!);
        this.selectedGroup.set(S25Util.deepCopy(group));

        // Store original contacts for differential changes
        this.originalContacts = S25Util.deepCopy(group.contacts || []);

        // Reset change tracking
        this.contactsToAdd = [];
        this.contactsToRemove = [];

        // Ensure contacts array exists
        if (!this.selectedGroup()!.contacts) {
            this.selectedGroup()!.contacts = [];
        }

        // Load contact names
        if (group.contacts && group.contacts.length > 0) {
            const contIds = group.contacts.map((c) => c.contId);
            const contactNames = await ContactService.getContactsNameAndFormal(contIds);
            const contactNameMap = new Map(contactNames.map((c) => [c.itemId, c.itemName]));
            this.selectedGroup()!.contacts!.forEach((c) => {
                c.contactName = contactNameMap.get(c.contId) || `Contact ${c.contId}`;
            });
            // Trigger change detection to update the UI with contact names
            this.cd.detectChanges();
        }
    }

    createNewGroup() {
        this.selectedGroupId.set(null);
        this.selectedGroup.set({
            name: "",
            contacts: [],
        });
        this.selectedContactForGroup = null;

        // Reset change tracking
        this.originalContacts = [];
        this.contactsToAdd = [];
        this.contactsToRemove = [];
    }

    cancelEdit() {
        this.selectedGroupId.set(null);
        this.selectedGroup.set(null);
        this.selectedContactForGroup = null;
    }

    onContactSelectedForGroup(contact: DropDownItem) {
        this.selectedContactForGroup = contact;
        this.addContactToGroup();
    }

    addContactToGroup() {
        if (!this.selectedContactForGroup) return;

        const group = this.selectedGroup();
        if (!group) return;

        if (!group.contacts) {
            group.contacts = [];
        }

        const newContactId = Number(this.selectedContactForGroup!.itemId);

        // Check if contact already exists
        const exists = group.contacts.some((c) => c.contId === newContactId);
        if (!exists) {
            const newContact: ApprovalGroupContact = {
                contId: newContactId,
                contactName: this.selectedContactForGroup!.itemName,
            };
            group.contacts.push(newContact);

            // Check if this contact was previously removed
            const removeIndex = this.contactsToRemove.findIndex((c) => c.contId === newContactId);
            if (removeIndex !== -1) {
                // Remove from remove list since we're adding it back
                this.contactsToRemove.splice(removeIndex, 1);
            } else {
                // Check if it wasn't in original contacts, so it's truly new
                const wasInOriginal = this.originalContacts.some((c) => c.contId === newContactId);
                if (!wasInOriginal) {
                    this.contactsToAdd.push(newContact);
                }
            }
        }

        // clear the contact from the dropdown outside the current cycle
        setTimeout(() => {
            this.selectedContactForGroup = null;
            this.cd.detectChanges();
        }, 0);
    }

    removeContactFromGroup(contactId: number) {
        const group = this.selectedGroup();
        if (group && group.contacts) {
            group.contacts = group.contacts.filter((c) => c.contId !== contactId);

            // Find the contact that was removed to track changes
            const contactToRemove = this.originalContacts.find((c) => c.contId === contactId);
            if (contactToRemove) {
                // Check if this contact was previously added
                const addIndex = this.contactsToAdd.findIndex((c) => c.contId === contactId);
                if (addIndex !== -1) {
                    // Remove from add list since we're removing it
                    this.contactsToAdd.splice(addIndex, 1);
                } else {
                    // Add to remove list since it wasn't previously added
                    this.contactsToRemove.push(contactToRemove);
                }
            }
        }
    }

    async saveGroup() {
        const group = this.selectedGroup();
        if (!group) return;

        if (!group.name.trim()) {
            S25Util.showError("Group name is required");
            return;
        }

        try {
            let result: ResponseMessage;
            if (this.isNewGroup()) {
                result = await ApprovalGroupService.createApprovalGroup(group);
            } else {
                // Update basic group fields first (excluding contacts)
                const groupUpdateData = { ...group };
                delete groupUpdateData.contacts; // Contacts will be handled separately
                result = await ApprovalGroupService.updateApprovalGroup(group.approvalGroupId!, groupUpdateData);

                // If group update succeeded and there are contact changes, update contacts differentially
                if (result.message && (this.contactsToAdd.length > 0 || this.contactsToRemove.length > 0)) {
                    const contactChanges: ApprovalGroupContactChanges = {
                        contactsToAdd: this.contactsToAdd,
                        contactsToRemove: this.contactsToRemove,
                    };
                    result = await ApprovalGroupService.updateApprovalGroupContacts(
                        group.approvalGroupId!,
                        contactChanges,
                    );
                }
            }

            if (result.message) {
                await this.refresh();
                this.cancelEdit();
            } else {
                S25Util.showError(result.message);
            }
        } catch (error) {
            console.error("Error saving approval group:", error);
            S25Util.showError(error);
        }
    }

    async refresh() {
        // Refresh table if available
        await this.groupsTableComponent?.refresh();
    }
}
